import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaShoppingCart, FaFileExcel } from "react-icons/fa";
import { toast } from "react-toastify";

import {
  fetchOrders,
  updateOrderStatus,
  updateOrderPaymentStatus,
  deleteOrder,
  getOrder,
  archiveOrder,
  archiveOrdersByDate,
  restoreOrder,
  clearState,
} from "../redux/Slices/OrderSlice";
import { useTitle } from "../context/TitleContext";
import PermissionButton from "../components/PermissionButton";
import axios from "axios";
import { showConfirm } from "../Utils/SwalConfirm";
import Pagination from '../components/Pagination';

// fixedOrderType: لو "book" أو "code" بتبقى صفحة مخصصة بدون tabs
const Orders = ({ fixedOrderType = null }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const [currentPage, setCurrentPage] = useState(1);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [archivingOrder, setArchivingOrder] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showArchiveByDateModal, setShowArchiveByDateModal] = useState(false);
  const [archiveDate, setArchiveDate] = useState("");
  const [archiveReason, setArchiveReason] = useState("");
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingExport, setLoadingExport] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [query, setQuery] = useState("");
  // فلاتر: التاريخ (يوم محدد) - حالة الدفع - حالة الطلب
  const [filterDate, setFilterDate] = useState("");
  const [filterPaymentStatus, setFilterPaymentStatus] = useState("");
  const [filterOrderStatus, setFilterOrderStatus] = useState("");
  const [dateFocused, setDateFocused] = useState(false);
  const [archiveDateFocused, setArchiveDateFocused] = useState(false);
  // نوع التب النشط: all | book | code | archived (مستخدم فقط لو fixedOrderType مش موجود)
  const [activeTab, setActiveTab] = useState("all");

  // لو fixedOrderType موجود → الـ tab دايماً مرتبط بيه
  const effectiveTab = fixedOrderType ? fixedOrderType : activeTab;

  const { orders, meta ,loading, error, success } = useSelector(
    (state) => state.orders
  );

  // Status options
  const statusOptions = [
    { value: "pending", label: "في الانتظار", color: "warning" },
    { value: "in_progress", label: "قيد التنفيذ", color: "info" },
    { value: "in_way", label: "في الطريق", color: "primary" },
    { value: "delivered", label: "تم التسليم", color: "success" },
    { value: "cancelled", label: "ملغي", color: "danger" },
  ];

  // Payment status options
  const paymentStatusOptions = [
    { value: "not_paid", label: "غير مدفوع", color: "danger" },
    { value: "paid", label: "مدفوع", color: "success" },
  ];

  // Payment status update options
  const paymentUpdateOptions = [
    { value: "not_paid", label: "غير مدفوع" },
    { value: "paid", label: "مدفوع" },
  ];

  // تجميع كل الـ params (البحث + الفلاتر) في مكان واحد
  const buildParams = (page) => ({
    page,
    search: query,
    archived: effectiveTab === "archived",
    date_from: filterDate,
    date_to: filterDate,
    payment_status: filterPaymentStatus,
    order_status: filterOrderStatus,
    type: effectiveTab === "book" ? "book" : effectiveTab === "code" ? "code" : effectiveTab === "product" ? "product" : "",
  });

  useEffect(() => {
    const titleMap = {
      book: "طلبات الكتب 📚",
      code: "طلبات الأكواد 🔑",
      product: "طلبات المنتجات 🛒",
      archived: "الطلبات المؤرشفة 📦",
    };
    setTitle(titleMap[effectiveTab] || "الطلبات");
  }, [setTitle, effectiveTab]);

  useEffect(() => {
    dispatch(clearState());
    dispatch(fetchOrders(buildParams(currentPage)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage, effectiveTab, filterDate, filterPaymentStatus, filterOrderStatus]);

  useEffect(() => {
    if (success) {
      // إغلاق modals فوراً عند النجاح
      setShowStatusModal(false);
      setShowDeleteModal(false);
      setShowDetailsModal(false);
      setShowArchiveModal(false);
      setShowArchiveByDateModal(false);
      setSelectedOrder(null);
      setDeletingOrder(null);
      setArchivingOrder(null);
      setOrderDetails(null);
      setArchiveReason("");

      // عرض toast بعد إغلاق الـ modals
      setTimeout(() => {
        toast.success(success, {
          position: "top-center",
          zIndex: 9999
        });
      }, 100);

      // إعادة تحميل البيانات بعد الحذف
      dispatch(fetchOrders(buildParams(currentPage)));
      dispatch(clearState());
    }
    if (error && error !== null) {
      // console.log("Orders error:", error);
      toast.error(error, {
        position: "top-center",
        zIndex: 9999
      });
      dispatch(clearState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, error, dispatch, currentPage]);

  const handlePageChange = (page) => {
    if (loading) return;
    if (page === currentPage) return;
    setCurrentPage(page);
  };

  const handleStatusUpdate = (order) => {
    // console.log("Starting status update for order:", order);
    // مسح أي أخطاء سابقة قبل البدء
    dispatch(clearState());
    setSelectedOrder(order);
    setShowStatusModal(true);
  };

  const handlePaymentUpdate = (order) => {
    // console.log("Starting payment update for order:", order);
    // مسح أي أخطاء سابقة قبل البدء
    dispatch(clearState());
    setSelectedOrder(order);
    setShowPaymentModal(true);
  };

  const handleDelete = (order) => {
    setDeletingOrder(order);
    setShowDeleteModal(true);
  };

  const handleArchive = (order) => {
    setArchivingOrder(order);
    setArchiveReason("");
    setShowArchiveModal(true);
  };

  const confirmArchive = () => {
    if (archivingOrder) {
      dispatch(archiveOrder({ id: archivingOrder.id, reason: archiveReason }));
    }
  };

  const confirmArchiveByDate = () => {
    if (archiveDate) {
      dispatch(archiveOrdersByDate({ date: archiveDate, reason: archiveReason }));
    } else {
      toast.warning("يرجى اختيار التاريخ");
    }
  };

  const handleRestore = async (order) => {
    const isConfirmed = await showConfirm({
      title: "استعادة الطلب",
      text: `هل أنت متأكد من استعادة الطلب #${order.order_number} من الأرشيف؟`,
      confirmButtonText: "نعم، استعادة",
      confirmButtonColor: "#28a745"
    });

    if (isConfirmed) {
      dispatch(restoreOrder(order.id));
    }
  };

  const handleViewDetails = async (order) => {
    try {
      const result = await dispatch(getOrder(order.id));
      if (result.payload) {
        setOrderDetails(result.payload.data);
        setShowDetailsModal(true);
      }
    } catch (error) {
      toast.error("خطأ في جلب تفاصيل الطلب");
    }
  };

  const confirmStatusUpdate = () => {
    if (selectedOrder) {
      const targetStatus = selectedOrder.newStatus || selectedOrder.order_status;
      if (targetStatus === "delivered" && selectedOrder.payment_status === "not_paid") {
        toast.error("لا يمكن تغيير حالة الطلب إلى تم التسليم دون تحصيل الأموال ودفع الطلب أولاً", {
          position: "top-center",
          zIndex: 9999
        });
        return;
      }
      dispatch(updateOrderStatus({
        id: selectedOrder.id,
        status: targetStatus
      }));
      setShowStatusModal(false);
      setSelectedOrder(null);
    }
  };

  const confirmPaymentUpdate = () => {
    if (selectedOrder) {
      dispatch(updateOrderPaymentStatus({
        id: selectedOrder.id,
        paymentStatus: selectedOrder.newPaymentStatus
      }));
      setShowPaymentModal(false);
      setSelectedOrder(null);
    }
  };

  const confirmDelete = () => {
    if (deletingOrder) {
      dispatch(deleteOrder(deletingOrder.id));
      setShowDeleteModal(false);
      setDeletingOrder(null);
    }
  };

  const getStatusBadge = (status, order) => {
    const isDigital = order?.is_digital || order?.type === "digital" || order?.order_type === "digital" || order?.type === "code" || order?.order_type === "code" || order?.category_name_en?.toLowerCase()?.includes("codes") || order?.items?.some(item => item.product?.category_name_en?.toLowerCase()?.includes("codes"));
    if (isDigital) {
      return (
        <span className="badge bg-success text-white">
          طلب رقمي (تم التسليم)
        </span>
      );
    }
    const statusOption = statusOptions.find(opt => opt.value === status);
    return (
      <span className={`badge bg-${statusOption?.color || 'secondary'} text-white`}>
        {statusOption?.label || status}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusOption = paymentStatusOptions.find(opt => opt.value === status);
    return (
      <span className={`badge bg-${statusOption?.color || 'secondary'} text-white`}>
        {statusOption?.label || status}
      </span>
    );
  };


  if (loading && orders?.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <div className="spinner-border text-danger" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
      </div>
    );
  }

  // export file
  const handleExport = async () => {
    const date_from = document.getElementById("dateFrom").value;
    const date_to = document.getElementById("dateTo").value;
    const payment_status = document.getElementById("paymentStatus").value;
    const order_status = document.getElementById("orderStatus").value;

    try {
      setLoadingExport(true);
      
      const getAuthToken = () => {
        try {
          const localData = localStorage.getItem("loggedUser");
          if (localData) {
            const parsed = JSON.parse(localData);
            if (parsed.token) return parsed.token;
            if (parsed.data?.token) return parsed.data.token;
            if (parsed.result?.token) return parsed.result.token;
            if (parsed.user?.token) return parsed.user.token;
          }
          const sessionData = sessionStorage.getItem("user") || sessionStorage.getItem("loggedUser");
          if (sessionData) {
            const parsed = JSON.parse(sessionData);
            if (parsed.token) return parsed.token;
            if (parsed.data?.token) return parsed.data.token;
            if (parsed.result?.token) return parsed.result.token;
            if (parsed.user?.token) return parsed.user.token;
          }
          return null;
        } catch {
          return null;
        }
      };

      const token = getAuthToken();
      if (!token) {
        toast.error("خطأ في المصادقة، يرجى تسجيل الدخول مجدداً");
        setLoadingExport(false);
        return;
      }

      const BASE_URL = process.env.REACT_APP_BASE_URL || "https://backend.mystore.education/api";
      const response = await axios.get(
        `${BASE_URL}/dashboard/exports/orders`,
        {
          params: { date_from, date_to, payment_status, order_status },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": "ar",
          },
        }
      );

      if (response.data?.status && response.data.data?.download_url) {
        const downloadUrl = response.data.data.download_url;
        window.open(downloadUrl, "_blank");
        setShowExportModal(false);
        toast.success("تم إنشاء ملف Excel بنجاح");
      } else {
        toast.warn(response.data?.message || "لم يتم العثور على رابط التحميل.");
      }
    } catch (error) {
      console.error(error);
      toast.error("حدث خطأ أثناء إنشاء الملف.");
    }
    finally {
      setLoadingExport(false);
    }
  };

  const handleSearchOrders = (e) => {
    e.preventDefault();
    if(!query || query.trim() === "") {
      toast.warning("يرجى إدخال رقم الهاتف أو رقم الطلب أو اسم العميل")
      return;
    }
    setCurrentPage(1);
    dispatch(fetchOrders({ ...buildParams(1), search: query }));
  }

  // عند تغيير أي فلتر نرجع لأول صفحة (الـ effect يعيد التحميل تلقائياً)
  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilterDate("");
    setFilterPaymentStatus("");
    setFilterOrderStatus("");
    setCurrentPage(1);
  };

  // تغيير التب النشط
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setQuery("");
    setFilterDate("");
    setFilterPaymentStatus("");
    setFilterOrderStatus("");
  };
  return (
    <div className="container-fluid" style={{ direction: "rtl", padding: "4px 0" }}>
      <style>{`
        .orders-tab-btn {
          padding: 8px 18px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: #64748b;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .orders-tab-btn.active.all {
          background: var(--red-color);
          color: #fff;
        }
        .orders-tab-btn.active.book {
          background: var(--warning-color);
          color: #fff;
        }
        .orders-tab-btn.active.code {
          background: #2563eb;
          color: #fff;
        }
        .orders-tab-btn.active.product {
          background: #16a34a;
          color: #fff;
        }
        .orders-tab-btn.active.archived {
          background: #64748b;
          color: #fff;
        }
        .ord-table {
          width: 100%;
          border-collapse: collapse;
          text-align: right;
        }
        .ord-table th {
          background-color: #f8fafc !important;
          color: #475569 !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          padding: 14px 16px !important;
          border-bottom: 1px solid #e2e8f0 !important;
          text-align: center !important;
        }
        .ord-table td {
          padding: 14px 16px !important;
          font-size: 14px !important;
          vertical-align: middle !important;
          border-bottom: 1px solid #f1f5f9 !important;
          text-align: center !important;
        }
        .ord-input {
          width: 100% !important;
          padding: 9px 14px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .ord-input:focus {
          border-color: var(--main-color) !important;
          box-shadow: 0 0 0 3px rgba(228, 109, 23, 0.1) !important;
        }
        .ord-custom-select {
          width: 100% !important;
          padding: 9px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .ord-custom-select:focus {
          border-color: var(--main-color) !important;
          box-shadow: 0 0 0 3px rgba(228, 109, 23, 0.1) !important;
        }
        .ord-custom-date {
          width: 100% !important;
          padding: 9px 14px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          color: #0f172a !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .ord-custom-date:focus {
          border-color: var(--main-color) !important;
          box-shadow: 0 0 0 3px rgba(228, 109, 23, 0.1) !important;
        }
        .ord-custom-date.empty {
          color: #94a3b8 !important;
        }
        .filter-label {
          font-size: 12px;
          font-weight: 700;
          color: #64748b;
          display: block;
          margin-bottom: 6px;
          text-align: right;
        }
      `}</style>
      <div className="row">
        <div className="col-12">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FaShoppingCart
                style={{ fontSize: "1.4rem", color: "var(--main-color)" }}
              />
              <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20 }}>
                {fixedOrderType === 'book' ? '📚 طلبات الكتب' : fixedOrderType === 'code' ? '🔑 طلبات الأكواد' : 'الطلبات'}
              </h4>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="btn text-white btn-sm d-flex align-items-center"
                onClick={() => setShowExportModal(true)}
                style={{ background: "var(--green-color)", fontWeight: 700, borderRadius: 9, padding: "8px 16px", border: "none" }}
              >
                <FaFileExcel className="me-1" /> تصدير Excel
              </button>
              <PermissionButton
                className="btn btn-secondary btn-sm"
                permission="archive-orders"
                onClick={() => {
                  setArchiveDate("");
                  setArchiveReason("");
                  setShowArchiveByDateModal(true);
                }}
                style={{ fontWeight: 700, borderRadius: 9, padding: "8px 16px" }}
              >
                أرشفة الطلبات بالتاريخ
              </PermissionButton>
            </div>
          </div>

          {/* ===== Tabs الرئيسية: تظهر بس في صفحة الطلبات العامة ===== */}
          {!fixedOrderType && (
            <>
              <div style={{ display: "flex", gap: 6, background: "#f1f5f9", padding: 4, borderRadius: 10, width: "fit-content", marginBottom: 20 }}>
                <button onClick={() => handleTabChange('all')} className={`orders-tab-btn${activeTab === 'all' ? ' active all' : ''}`}>
                  📋 كل الطلبات
                </button>
                <button onClick={() => handleTabChange('book')} className={`orders-tab-btn${activeTab === 'book' ? ' active book' : ''}`}>
                  📚 طلبات الكتب
                </button>
                <button onClick={() => handleTabChange('code')} className={`orders-tab-btn${activeTab === 'code' ? ' active code' : ''}`}>
                  🔑 طلبات الأكواد
                </button>
                <button onClick={() => handleTabChange('product')} className={`orders-tab-btn${activeTab === 'product' ? ' active product' : ''}`}>
                  🛒 طلبات المنتجات
                </button>
                <button onClick={() => handleTabChange('archived')} className={`orders-tab-btn${activeTab === 'archived' ? ' active archived' : ''}`}>
                  📦 المؤرشفة
                </button>
              </div>

              {/* badge يوضح نوع الطلبات الحالي */}
              {activeTab !== 'all' && (
                <div className="mb-3">
                  {activeTab === 'book' && (
                    <span className="badge" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.85rem', padding: '6px 12px', borderRadius: '20px' }}>
                      📚 عرض طلبات الكتب الدراسية فقط
                    </span>
                  )}
                  {activeTab === 'code' && (
                    <span className="badge" style={{ background: '#dbeafe', color: '#1e40af', fontSize: '0.85rem', padding: '6px 12px', borderRadius: '20px' }}>
                      🔑 عرض طلبات الأكواد الرقمية فقط
                    </span>
                  )}
                  {activeTab === 'product' && (
                    <span className="badge" style={{ background: '#dcfce7', color: '#166534', fontSize: '0.85rem', padding: '6px 12px', borderRadius: '20px' }}>
                      🛒 عرض طلبات المنتجات العامة فقط
                    </span>
                  )}
                  {activeTab === 'archived' && (
                    <span className="badge bg-secondary" style={{ fontSize: '0.85rem', padding: '6px 12px', borderRadius: '20px' }}>
                      📦 عرض الطلبات المؤرشفة
                    </span>
                  )}
                </div>
              )}
            </>
          )}

          {/* badge ثابت للصفحات المخصصة */}
          {fixedOrderType && (
            <div className="mb-3">
              {fixedOrderType === 'book' && (
                <span className="badge" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.9rem', padding: '8px 16px', borderRadius: '20px' }}>
                  📚 طلبات الكتب الدراسية فقط
                </span>
              )}
              {fixedOrderType === 'code' && (
                <span className="badge" style={{ background: '#dbeafe', color: '#1e40af', fontSize: '0.9rem', padding: '8px 16px', borderRadius: '20px' }}>
                  🔑 طلبات الأكواد الرقمية فقط
                </span>
              )}
            </div>
          )}

          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "18px 22px", marginBottom: 20 }}>
             <div className="search-area">
                <label className="filter-label" style={{ fontSize: 13, marginBottom: 8 }}>البحث السريع في الطلبات</label>
                <form className="d-flex my-2 align-items-center" onSubmit={handleSearchOrders} style={{ maxWidth: 500, gap: 10 }}>
                  <input className="ord-input" value={query}
                    onChange={(e) => setQuery(e.target.value)} placeholder="رقم الهاتف، رقم الطلب، أو اسم العميل..." />
                  <button className="btn btn-primary btn-sm" type="submit" style={{ padding: "10px 18px", borderRadius: 9 }}><i className="bi bi-search"></i></button>
                </form>

                {/* فلاتر: التاريخ / حالة الطلب / حالة الدفع */}
                <div className="row g-3 align-items-end mt-2">
                  <div className="col-md-4 col-12">
                    <label className="filter-label">التاريخ (طلبات يوم معين)</label>
                    <input
                      type={dateFocused || filterDate ? "date" : "text"}
                      placeholder="اختر تاريخ اليوم"
                      className="ord-custom-date"
                      value={filterDate}
                      max={new Date().toISOString().split("T")[0]}
                      onFocus={() => setDateFocused(true)}
                      onBlur={() => setDateFocused(false)}
                      onChange={(e) => handleFilterChange(setFilterDate)(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4 col-6">
                    <label className="filter-label">حالة الطلب</label>
                    <select
                      className="ord-custom-select"
                      value={filterOrderStatus}
                      onChange={(e) => handleFilterChange(setFilterOrderStatus)(e.target.value)}
                    >
                      <option value="">الكل</option>
                      {statusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4 col-6">
                    <label className="filter-label">حالة الدفع</label>
                    <select
                      className="ord-custom-select"
                      value={filterPaymentStatus}
                      onChange={(e) => handleFilterChange(setFilterPaymentStatus)(e.target.value)}
                    >
                      <option value="">الكل</option>
                      {paymentStatusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12" style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleClearFilters}
                      disabled={!filterDate && !filterPaymentStatus && !filterOrderStatus}
                      style={{ padding: "8px 20px", borderRadius: 9 }}
                    >
                      <i className="bi bi-x-circle me-1"></i> مسح الفلاتر
                    </button>
                  </div>
                </div>
             </div>
          </div>

          {/* Orders Table */}
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
            <div className="card-body p-0">
              {orders?.length === 0 ? (
                <div className="text-center py-5">
                  <FaShoppingCart size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">لا يوجد طلبات</h5>
                  <p className="text-muted">لم يتم العثور على أي طلبات</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="ord-table">
                      <thead className="table-light">
                        <tr>
                          <th className="text-center">#</th>
                          <th className="text-center">رقم الطلب</th>
                          <th className="text-center">العميل</th>
                          <th className="text-center">الهاتف</th>
                          {/* عمود المدينة/المنطقة يظهر بس في طلبات الكتب والكل */}
                          {effectiveTab !== 'code' && (
                            <>
                              <th className="text-center">المدينة</th>
                              <th className="text-center">المنطقة</th>
                            </>
                          )}
                          {/* عمود نوع الطلب يظهر بس في تب الكل */}
                          {effectiveTab === 'all' && (
                            <th className="text-center">نوع الطلب</th>
                          )}
                          <th className="text-center">المجموع</th>
                          <th className="text-center">حالة الطلب</th>
                          <th className="text-center">حالة الدفع</th>
                          <th className="text-center">التاريخ</th>
                          <th className="text-center">الإجراءات</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders?.map((order, index) => (
                          <tr key={order.id}>
                            <td className="text-center">
                              {meta
                                ? (meta.current_page - 1) * meta.per_page + index + 1
                                : index + 1}
                            </td>
                            <td className="text-center">
                              <strong className="text-primary">
                                {order.order_number?.substring(0, 8)}
                              </strong>
                            </td>
                            <td className="text-center">
                              {(() => {
                                const userId = order.user_id ?? order.user?.id;
                                return userId ? (
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 fw-bold text-decoration-none"
                                    onClick={() => navigate(`/user-details/${userId}`)}
                                    title="عرض جميع معاملات العميل"
                                  >
                                    {order.user_name}
                                  </button>
                                ) : (
                                  <strong>{order.user_name}</strong>
                                );
                              })()}
                            </td>
                            <td className="text-center">
                              <span className="badge bg-info text-white">
                                {order.phone}
                              </span>
                            </td>
                            {/* عمود المدينة والمنطقة: يظهر في كل + كتب (للكودات مش مهم) */}
                            {effectiveTab !== 'code' && (
                              <>
                                <td className="text-center">
                                  <span className="badge bg-secondary text-white">
                                    {order.city_name_ar}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <span className="badge bg-light text-dark">
                                    {order.region_name_ar}
                                  </span>
                                </td>
                              </>
                            )}
                            {/* عمود نوع الطلب: يظهر في تب الكل فقط */}
                            {effectiveTab === 'all' && (
                              <td className="text-center">
                                {(order.type === 'code' || order.order_type === 'code') ? (
                                  <span className="badge bg-primary text-white">🔑 كود</span>
                                ) : (order.type === 'book' || order.order_type === 'book') ? (
                                  <span className="badge bg-warning text-dark">📚 كتاب</span>
                                ) : (order.type === 'product' || order.order_type === 'product') ? (
                                  <span className="badge bg-success text-white">🛒 منتج</span>
                                ) : (
                                  <span className="badge bg-secondary text-white">—</span>
                                )}
                              </td>
                            )}
                            <td className="text-center">
                              {/* BUG-06 fix: عرض السعر النهائي فقط بشكل واضح، مع السعر الأصلي (مشطوب) في سطر منفصل عند وجود خصم */}
                              <div>
                                <strong className="text-success">
                                  {order.total_after_discount ?? order.grand_total} ج.م
                                </strong>
                                {order.total_before_discount != null && order.total_before_discount !== order.total_after_discount && (
                                  <div>
                                    <small className="text-muted text-decoration-line-through">
                                      {order.total_before_discount} ج.م
                                    </small>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="text-center">
                              {getStatusBadge(order.order_status, order)}
                            </td>
                            <td className="text-center">
                              {getPaymentStatusBadge(order.payment_status)}
                            </td>
                            <td className="text-center">
                              <small className="text-muted">
                                {new Date(order.created_at).toLocaleDateString('ar-EG')}
                              </small>
                            </td>
                            <td className="text-center">
                              <div style={{ display: "inline-flex", gap: 5, justifyContent: "center" }}>
                                <button
                                  onClick={() => handleViewDetails(order)}
                                  title="عرض التفاصيل"
                                  style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                  👁
                                </button>
                                <PermissionButton
                                  onClick={() => handleStatusUpdate(order)}
                                  permission="change-status-orders"
                                  title="تحديث حالة الطلب"
                                  style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                  ✏
                                </PermissionButton>
                                <PermissionButton
                                  onClick={() => handlePaymentUpdate(order)}
                                  permission="change-payment-status-orders"
                                  title="تحديث حالة الدفع"
                                  style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#16a34a", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                  💳
                                </PermissionButton>
                                <PermissionButton
                                  onClick={() => handleDelete(order)}
                                  permission="delete-orders"
                                  disabled={loading}
                                  title="حذف"
                                  style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                                >
                                  🗑
                                </PermissionButton>
                                {activeTab !== 'archived' && (
                                  <PermissionButton
                                    onClick={() => handleArchive(order)}
                                    permission="archive-orders"
                                    title="أرشفة"
                                    style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #cbd5e1", background: "#f8fafc", color: "#475569", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                                  >
                                    📦
                                  </PermissionButton>
                                )}
                                {activeTab === 'archived' && (
                                  <PermissionButton
                                    onClick={() => handleRestore(order)}
                                    permission="archive-orders"
                                    title="استعادة"
                                    style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#16a34a", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                                  >
                                    🔄
                                  </PermissionButton>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination meta={meta} currentPage={currentPage} loading={loading} onChange={handlePageChange} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && selectedOrder && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">تحديث حالة الطلب</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowStatusModal(false);
                    setSelectedOrder(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">رقم الطلب: <strong>#{selectedOrder.order_number}</strong></label>
                </div>
                <div className="mb-3">
                  <label className="form-label">العميل: <strong>{selectedOrder.user_name}</strong></label>
                </div>
                <div className="mb-3">
                  <label className="form-label">الحالة الحالية: {getStatusBadge(selectedOrder.order_status)}</label>
                </div>
                <div className="mb-3">
                  <label className="form-label">اختر الحالة الجديدة *</label>
                  <select
                    className="form-select"
                    value={selectedOrder.newStatus || selectedOrder.order_status}
                    onChange={(e) => setSelectedOrder({ ...selectedOrder, newStatus: e.target.value })}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowStatusModal(false);
                    setSelectedOrder(null);
                  }}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={confirmStatusUpdate}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      جاري التحديث...
                    </>
                  ) : (
                    'تحديث الحالة'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Status Update Modal */}
      {showPaymentModal && selectedOrder && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">تحديث حالة الدفع</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedOrder(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">رقم الطلب: <strong>#{selectedOrder.order_number}</strong></label>
                </div>
                <div className="mb-3">
                  <label className="form-label">العميل: <strong>{selectedOrder.user_name}</strong></label>
                </div>
                <div className="mb-3">
                  <label className="form-label">الحالة الحالية: {getPaymentStatusBadge(selectedOrder.payment_status)}</label>
                </div>
                <div className="mb-3">
                  <label className="form-label">اختر حالة الدفع الجديدة *</label>
                  <select
                    className="form-select"
                    value={selectedOrder.newPaymentStatus || selectedOrder.payment_status}
                    onChange={(e) => setSelectedOrder({ ...selectedOrder, newPaymentStatus: e.target.value })}
                  >
                    {paymentUpdateOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedOrder(null);
                  }}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={confirmPaymentUpdate}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      جاري التحديث...
                    </>
                  ) : (
                    'تحديث حالة الدفع'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showDetailsModal && orderDetails && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header" >
                <h5 className="modal-title w-100">تفاصيل الطلب    #{orderDetails.order_number}</h5>
                <p></p>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDetailsModal(false);
                    setOrderDetails(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>معلومات العميل</h6>
                    <p><strong>الاسم:</strong> {orderDetails.user_name}</p>
                    <p><strong>الهاتف:</strong> {orderDetails.phone}</p>
                    <p><strong>العنوان:</strong> {orderDetails.address}</p>
                    <p><strong>المدينة:</strong> {orderDetails.city_name_ar}</p>
                    <p><strong>المنطقة:</strong> {orderDetails.region_name_ar}</p>
                  </div>
                  <div className="col-md-6">
                    <h6>معلومات الطلب</h6>
                    <p><strong>رقم الطلب:</strong> {orderDetails.order_number}</p>
                    <p><strong>نوع الطلب:</strong>{' '}
                      {(orderDetails.type === 'code' || orderDetails.order_type === 'code') ? (
                        <span className="badge bg-primary">🔑 كود رقمي</span>
                      ) : (orderDetails.type === 'book' || orderDetails.order_type === 'book') ? (
                        <span className="badge bg-warning text-dark">📚 كتاب</span>
                      ) : (orderDetails.type === 'product' || orderDetails.order_type === 'product') ? (
                        <span className="badge bg-success">🛒 منتج</span>
                      ) : (
                        <span className="badge bg-secondary">—</span>
                      )}
                    </p>
                    <p><strong>نوع الدفع:</strong> {orderDetails.payment_type === 'cash' ? 'نقدي' : 'إلكتروني'}</p>
                    <p><strong>حالة الطلب:</strong> {getStatusBadge(orderDetails.order_status)}</p>
                    <p><strong>حالة الدفع:</strong> {getPaymentStatusBadge(orderDetails.payment_status)}</p>
                    <p><strong>التاريخ:</strong> {new Date(orderDetails.created_at).toLocaleDateString('ar-EG')}</p>
                  </div>
                </div>
                <hr />
                <h6>المنتجات</h6>
                <div className="table-responsive">
                  <table className="table table-sm" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                        <th style={{ textAlign: "right", padding: "12px 8px", color: "#475569", fontWeight: 700 }}>المنتج</th>
                        <th style={{ textAlign: "center", padding: "12px 8px", color: "#475569", fontWeight: 700 }}>الكمية</th>
                        <th style={{ textAlign: "center", padding: "12px 8px", color: "#475569", fontWeight: 700 }}>السعر</th>
                        <th style={{ textAlign: "center", padding: "12px 8px", color: "#475569", fontWeight: 700 }}>المجموع</th>
                        <th style={{ textAlign: "center", padding: "12px 8px", color: "#475569", fontWeight: 700 }}>الحالة</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const itemsList = orderDetails.items || orderDetails.products || [];
                        return itemsList.map((item, index) => {
                          const productTitleAr = item.product?.title_ar || item.product_name || item.title_ar || "منتج غير معروف";
                          const productTitleEn = item.product?.title_en || item.product_name || item.title_en || "Unknown Product";
                          const itemPrice = parseFloat(item.product?.price || item.price || 0);
                          const itemQty = parseInt(item.qty || item.quantity || 1);
                          const itemTotal = itemPrice * itemQty;

                          return (
                            <tr key={item.id || index} style={{ borderBottom: "1px solid #f1f5f9" }}>
                              <td style={{ textAlign: "right", padding: "12px 8px", verticalAlign: "middle" }}>
                                <strong style={{ color: "#0f172a" }}>{productTitleAr}</strong>
                                <br />
                                <small className="text-muted">{productTitleEn}</small>
                              </td>
                              <td style={{ textAlign: "center", padding: "12px 8px", verticalAlign: "middle" }}>
                                <span className="badge bg-secondary text-dark" style={{ background: "#f1f5f9", padding: "4px 8px", borderRadius: 6 }}>{itemQty}</span>
                              </td>
                              <td style={{ textAlign: "center", padding: "12px 8px", verticalAlign: "middle", color: "#475569" }}>{itemPrice} ج.م</td>
                              <td style={{ textAlign: "center", padding: "12px 8px", verticalAlign: "middle", fontWeight: 600, color: "#16a34a" }}>{itemTotal} ج.م</td>
                              <td style={{ textAlign: "center", padding: "12px 8px", verticalAlign: "middle" }}>{getStatusBadge(item.status || orderDetails.order_status, orderDetails)}</td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
                <div className="row mt-3">
                  <div className="col-md-6">
                    <p><strong>المجموع قبل الخصم:</strong> {orderDetails.total_before_discount} ج.م</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>المجموع بعد الخصم:</strong> {orderDetails.total_after_discount} ج.م</p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDetailsModal(false);
                    setOrderDetails(null);
                  }}
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingOrder && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">تأكيد الحذف</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingOrder(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>هل أنت متأكد من حذف هذا الطلب؟</p>
                <p><strong>رقم الطلب:</strong> #{deletingOrder.order_number}</p>
                <p><strong>العميل:</strong> {deletingOrder.user_name}</p>
                <p className="text-danger">هذا الإجراء لا يمكن التراجع عنه.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingOrder(null);
                  }}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      جاري الحذف...
                    </>
                  ) : (
                    'حذف الطلب'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive Modal */}
      {showArchiveModal && archivingOrder && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">أرشفة الطلب</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowArchiveModal(false);
                    setArchivingOrder(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>هل أنت متأكد من أرشفة الطلب #{archivingOrder.order_number}؟</p>
                <div className="mb-3">
                  <label className="form-label">سبب الأرشفة (اختياري)</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={archiveReason}
                    onChange={(e) => setArchiveReason(e.target.value)}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowArchiveModal(false);
                    setArchivingOrder(null);
                  }}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={confirmArchive}
                  disabled={loading}
                >
                  {loading ? 'جاري الأرشفة...' : 'أرشفة الطلب'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Excel Modal */}
      {showExportModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">تصدير الطلبات إلى Excel</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowExportModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-muted small">حدّد الفلاتر المطلوبة (اتركها فارغة لتصدير الكل).</p>
                <div className="mb-3">
                  <label className="form-label">من تاريخ</label>
                  <input type="date" className="form-control" id="dateFrom" name="date_from" />
                </div>
                <div className="mb-3">
                  <label className="form-label">إلى تاريخ</label>
                  <input type="date" className="form-control" id="dateTo" name="date_to" />
                </div>
                <div className="mb-3">
                  <label className="form-label">حالة الدفع</label>
                  <select className="form-select" id="paymentStatus" name="payment_status" defaultValue="">
                    <option value="">الكل</option>
                    {paymentStatusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">حالة الطلب</label>
                  <select className="form-select" id="orderStatus" name="order_status" defaultValue="">
                    <option value="">الكل</option>
                    {statusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowExportModal(false)}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleExport}
                  disabled={loadingExport}
                >
                  {loadingExport ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      جاري التصدير...
                    </>
                  ) : (
                    <><FaFileExcel className="me-1" /> تصدير</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive By Date Modal */}
      {showArchiveByDateModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">أرشفة الطلبات بالتاريخ</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowArchiveByDateModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>سيتم أرشفة جميع الطلبات حتى التاريخ المحدد.</p>
                <div className="mb-3">
                  <label className="form-label">التاريخ *</label>
                  <input
                    type={archiveDateFocused || archiveDate ? "date" : "text"}
                    placeholder="اختر تاريخ الأرشفة"
                    className="form-control"
                    value={archiveDate}
                    onFocus={() => setArchiveDateFocused(true)}
                    onBlur={() => setArchiveDateFocused(false)}
                    onChange={(e) => setArchiveDate(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">سبب الأرشفة (اختياري)</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={archiveReason}
                    onChange={(e) => setArchiveReason(e.target.value)}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowArchiveByDateModal(false)}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={confirmArchiveByDate}
                  disabled={loading || !archiveDate}
                >
                  {loading ? 'جاري الأرشفة...' : 'أرشفة الطلبات'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Orders;
